import React, { useState, useEffect, useCallback, useRef } from 'react'
import {
  Box,
  Typography,
  Button,
  Fade,
  Select,
  MenuItem,
  Tooltip,
  IconButton,
  Paper,
  FormControl,
  InputLabel,
  Switch,
  FormControlLabel
} from '@mui/material'
import FileUploadIcon from '@mui/icons-material/FileUpload'
import MicIcon from '@mui/icons-material/Mic'
import StopCircleIcon from '@mui/icons-material/StopCircle'
import TextDecreaseIcon from '@mui/icons-material/TextDecrease'
import TextIncreaseIcon from '@mui/icons-material/TextIncrease'
import ContentCopyIcon from '@mui/icons-material/ContentCopy'
import TvIcon from '@mui/icons-material/Tv'
import PlayArrowIcon from '@mui/icons-material/PlayArrow'
import PauseIcon from '@mui/icons-material/Pause'
import RecordVoiceOverIcon from '@mui/icons-material/RecordVoiceOver'
import superagent from 'superagent'
import { useAlert, useAuth } from '../App'
import useRecorder from './recorder'
import { useTheme } from '@mui/material/styles'
import { useTranslation } from 'react-i18next'
import TranscriptionHistory from '../components/TranscriptionHistory'
import LiveTranscription from '../components/LiveTranscription'
import TeleprompterOverlay from '../components/TeleprompterOverlay'
import { transcriptionStorage, TranscriptionRecord } from '../utils/transcriptionStorage'
import { getTranslations, getUILanguage } from '../utils/translations'
import { convertLanguageCode, type ASRLanguage, asrLanguageToUILanguage } from '../utils/localeUtils'
import { tokens } from '../theme/tokens'

const API_BASE = window.__ENV__?.API_BASE || 'http://localhost:3000'

interface TranscriptionSegment {
  text: string;
  type: 'partial' | 'final';
  segment?: number;
  start?: number;
  end?: number;
  sentence_id?: number;
}

const Form = () => {
  const [dictationMode, setDictationMode] = useState(() => {
    const saved = localStorage.getItem('dictationMode')
    return saved === 'true'
  })

  const [
    audioURL,
    isRecording,
    startRecording,
    stopRecording,
    language,
    handleLanguageChange,
    responseSocketVoxist,
    setResponseSocketVoxist,
    setAudioURL,
    model
  ] = useRecorder(dictationMode)
  
  const { setAlert } = useAlert()
  const auth = useAuth()
  const { i18n } = useTranslation()
  const theme = useTheme()
  const isDark = theme.palette.mode === 'dark'
  
  // State management
  const [loading, setLoading] = useState(false)
  const [file, setFile] = useState<File | null>(null)
  const [fontSize, setFontSize] = useState(24)
  const [currentTranscription, setCurrentTranscription] = useState<TranscriptionSegment[]>([])
  const [selectedTranscription, setSelectedTranscription] = useState<TranscriptionRecord | null>(null)
  const [historyRefreshKey, setHistoryRefreshKey] = useState(0)
  const [hasSavedCurrentRecording, setHasSavedCurrentRecording] = useState(false)
  const [showTeleprompter, setShowTeleprompter] = useState(false)
  const [isPlaying, setIsPlaying] = useState(false)
  const [currentAudioUrl, setCurrentAudioUrl] = useState<string | null>(null)
  const latestTranscriptionRef = useRef<TranscriptionSegment[]>([])
  const audioPlayerRef = useRef<HTMLAudioElement>(null)
  
  // Get UI language and translations
  const uiLanguage = getUILanguage(language)
  const t = getTranslations(uiLanguage)

  // Enhanced language change handler that updates both ASR and UI language
  const handleLanguageChangeWithUI = (newLanguage: ASRLanguage) => {
    // Update ASR language
    handleLanguageChange(newLanguage)
    
    // Update UI language
    const newUILanguage = asrLanguageToUILanguage(newLanguage)
    i18n.changeLanguage(newUILanguage)
  }

  // Update current audio URL when audioURL changes (from recording)
  useEffect(() => {
    if (audioURL && !selectedTranscription) {
      setCurrentAudioUrl(audioURL);
    }
  }, [audioURL, selectedTranscription]);

  // Process real-time transcription data
  useEffect(() => {
    if (responseSocketVoxist && Array.isArray(responseSocketVoxist)) {
      
      // Update transcription segments properly
      setCurrentTranscription(prevSegments => {
        // Create a copy of existing segments
        const updatedSegments = [...prevSegments];
        
        // Process each new segment
        responseSocketVoxist.forEach((item: any) => {
          const newSegment: TranscriptionSegment = {
            text: item.text || '',
            type: item.type === 'final' ? 'final' : 'partial',
            segment: item.segment,
            start: item.startedAt || item.start,  // Handle both startedAt and start
            end: item.end,
            sentence_id: item.sentence_id
          };
          
          if (item.segment !== undefined) {
            // Find if this segment already exists
            const existingIndex = updatedSegments.findIndex(s => s.segment === item.segment);
            
            if (existingIndex >= 0) {
              // Update existing segment
              updatedSegments[existingIndex] = newSegment;
            } else {
              // Add new segment in the correct position (sorted by segment number)
              const insertIndex = updatedSegments.findIndex(s => (s.segment || 0) > item.segment);
              if (insertIndex >= 0) {
                updatedSegments.splice(insertIndex, 0, newSegment);
              } else {
                updatedSegments.push(newSegment);
              }
            }
          } else {
            // If no segment number, just append
            updatedSegments.push(newSegment);
          }
        });
        
        // Update the ref with latest segments
        latestTranscriptionRef.current = updatedSegments;
        return updatedSegments;
      });
    }
  }, [responseSocketVoxist]);

  // Save transcription when recording stops (with delay to get final segments)
  useEffect(() => {
    if (!isRecording && currentTranscription.length > 0 && audioURL && !hasSavedCurrentRecording) {
      // Wait a bit longer to ensure we get all final segments from the WebSocket
      const saveTimeout = setTimeout(() => {
        const finalSegments = latestTranscriptionRef.current.filter(s => s.type === 'final');
        const finalText = finalSegments.map(s => s.text).join(' ');
          
        if (finalText.trim()) {
          transcriptionStorage.saveTranscription({
            text: finalText,
            language,
            audioUrl: audioURL,
            isRealTime: true,
            segments: finalSegments // Save segments with timing data
          }).then(() => {
            setHasSavedCurrentRecording(true); // Mark as saved
            setHistoryRefreshKey(prev => prev + 1); // Trigger history refresh
            // Keep the transcription visible - don't clear it
          }).catch(error => {
            setHasSavedCurrentRecording(true); // Still mark as saved to avoid retry
            setHistoryRefreshKey(prev => prev + 1);
          });
        }
      }, 2000); // Wait 2 seconds after recording stops to get final segments

      return () => clearTimeout(saveTimeout);
    }
  }, [isRecording, audioURL, language, hasSavedCurrentRecording, currentTranscription.length]);

  const processFileTranscription = useCallback(async (fileToProcess: File) => {
    if (!fileToProcess) {
      return;
    }
    
    setLoading(true);
    setCurrentTranscription([]);
    
    try {
      const token = await auth.getToken();
      
      // Create FormData for file upload
      const formData = new FormData();
      formData.append('file_channel1', fileToProcess);
      
      // Create config object with simplified parameters
      const config = {
        lang: convertLanguageCode(language),
        sample_rate: 16000,
        wait: true
      };
      formData.append('config', JSON.stringify(config));

      const url = `${API_BASE}/transcription`;

      const response = await superagent
        .post(url)
        .auth(token, { type: 'bearer' })
        .send(formData);
      
      // Handle new transformed response format
      let transcriptionText = '';
      let transcriptionSegments = [];
      
      console.log('Response received:', response.body);
      
      if (response.body?.text) {
        // New format with transformed structure
        transcriptionText = response.body.text;
        console.log('Using new format, transcriptionText:', transcriptionText);
        if (response.body.elements?.segments) {
          transcriptionSegments = response.body.elements.segments.map(segment => ({
            text: segment.text,
            type: 'final' as const,
            start: segment.startedAt,
            segment: segment.segment
          }));
        }
      } else if (Array.isArray(response.body)) {
        // Legacy format - array of objects with lexical field
        transcriptionText = response.body.map(item => item.lexical || '').join(' ');
        console.log('Using legacy array format, transcriptionText:', transcriptionText);
      } else if (response.body?.lexical) {
        // Legacy format - single object with lexical field
        transcriptionText = response.body.lexical;
        console.log('Using legacy object format, transcriptionText:', transcriptionText);
      }
      
      console.log('Final transcriptionText:', transcriptionText, 'Length:', transcriptionText.length);
      
      if (transcriptionText && transcriptionText.trim()) {
        // Use segments from new format if available, otherwise create basic segments
        const fileSegments = transcriptionSegments.length > 0 ? 
          transcriptionSegments : 
          [{
            text: transcriptionText,
            type: 'final' as const
          }];
        
        // Save to history
        transcriptionStorage.saveTranscription({
          text: transcriptionText,
          language,
          isRealTime: false,
          segments: fileSegments
        }).catch(() => {});
        
        // Display as final transcription
        setCurrentTranscription(fileSegments);
        
        // Trigger history refresh
        setHistoryRefreshKey(prev => prev + 1);
      } else {
        console.log('No transcription text found. Response body structure:', {
          hasText: !!response.body?.text,
          textValue: response.body?.text,
          isArray: Array.isArray(response.body),
          hasLexical: !!response.body?.lexical,
          keys: response.body ? Object.keys(response.body) : 'no body'
        });
        setAlert({ type: 'warning', text: 'No transcription received from server' });
      }
      
      setLoading(false);
    } catch (error: any) {
      
      let errorMessage = t.cantReachServer;
      if (error.response?.status === 401) {
        errorMessage = 'Authentication failed. Please log in again.';
      } else if (error.response?.status === 402) {
        errorMessage = 'Insufficient wallet balance. Please top up your wallet.';
      } else if (error.response?.status === 413) {
        errorMessage = 'File too large. Please select a smaller file.';
      } else if (error.response?.body?.message) {
        errorMessage = error.response.body.message;
      }
      
      setAlert({ type: 'error', text: errorMessage });
      setLoading(false);
    }
  }, [auth, language, setAlert, t]);

  // Handle file upload - process immediately to avoid dependency loops
  const handleFileUpload = useCallback(async (event: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFile = event.target.files?.[0];
    
    if (selectedFile && !loading) { // Prevent processing if already loading
      setFile(selectedFile);
      setSelectedTranscription(null); // Clear selected transcription when uploading new file
      
      // Reset the input value to allow selecting the same file again
      event.target.value = '';
      
      // Process the file immediately to avoid dependency loops
      await processFileTranscription(selectedFile);
    }
  }, [processFileTranscription, loading]);

  // Handle recording
  const handleStartRecord = useCallback(() => {
    // Clear everything for a new recording session
    setCurrentTranscription([]);
    setSelectedTranscription(null);
    setFile(null);
    setAudioURL(null);
    setCurrentAudioUrl(null);
    setHasSavedCurrentRecording(false); // Reset save flag for new recording
    latestTranscriptionRef.current = []; // Reset ref for new recording
    setResponseSocketVoxist(null); // Clear any previous socket response
    startRecording();
  }, [startRecording, setAudioURL, setResponseSocketVoxist]);

  // Font size controls
  const increaseFontSize = () => {
    if (fontSize < 48) setFontSize(prev => prev + 2);
  };

  const decreaseFontSize = () => {
    if (fontSize > 12) setFontSize(prev => prev - 2);
  };

  // Dictation mode toggle handler
  const handleDictationToggle = (event: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = event.target.checked
    setDictationMode(newValue)
    localStorage.setItem('dictationMode', String(newValue))
  }

  // Copy transcription text
  const copyTranscription = () => {
    const text = selectedTranscription?.text || 
      currentTranscription.map(s => s.text).join(' ');
    
    if (text && navigator.clipboard) {
      navigator.clipboard.writeText(text);
      setAlert({ type: 'success', text: t.textCopied });
    }
  };

  // Get teleprompter text with formatting
  const getTeleprompterText = (): string => {
    const rawText = selectedTranscription?.text || 
      currentTranscription
        .filter(s => s.type === 'final')
        .map(s => s.text)
        .join(' ');
    
    if (!rawText) return '';
    
    // Apply French text formatting rules similar to the original teleprompter
    return rawText
      .replace(/(ouvrez|ouvrer|ouvré|ouvrir|ouvre|ouvres) la (parenthèse|parenthese)/gimu, '(')
      .replace(/(fermer|fermez|fermé|ferme|fermes) la (parenthèse|parenthese)/gimu, ')')
      .replace(/(ouvrez|ouvrer|ouvré|ouvrir|ouvre|ouvres) les (guillemets|guillemet|guillemais)/gimu, '«')
      .replace(/(fermer|fermez|fermé|ferme|fermes) les (guillemets|guillemet|guillemais)/gimu, '»')
      .replace(/a la ligne|à la ligne|retour ligne|retour à la ligne|retour a la ligne/gimu, '\n')
      .replace(/points|point/gimu, '.')
      .replace(/deux points|2 point|2 points|deux point|2 \.|deux \./gimu, ':')
      .replace(/:/gimu, ':\n')
      .replace(/virgule/gimu, ',')
      .replace(/point virgule|\.,|\.-,/gimu, ';')
      .replace(/\.,|\.-,|\. ,/gimu, ';')
      .replace(/poin;/gimu, ';')
      .replace(/(\.[a-z])|(\. [a-z])/gimu, (match) => match.toUpperCase())
      .replace(/(:[a-z])|(: [a-z])|(:\n[a-z])/gimu, (match) => match.toUpperCase())
      .replace(/\n\s*?\p{Ll}/gimu, (match) => match.toUpperCase())
      .replace(/\s+([.,])/gimu, '$1')
      .replace(/(:)/gimu, ' $1')
      .replace(/(;)/gimu, ' $1')
      .replace(/\s+(\))/gimu, '$1')
      .replace(/(\()\s+/gimu, '$1')
      .replace(/\./gimu, '.\n');
  };

  // Handle transcription selection from history
  const handleTranscriptionSelect = async (transcription: TranscriptionRecord) => {
    setSelectedTranscription(transcription);
    
    // Get fresh audio URL for playback
    let audioUrl = transcription.audioUrl;
    if (!audioUrl) {
      audioUrl = await transcriptionStorage.getAudioUrl(transcription.id);
    }
    
    setCurrentAudioUrl(audioUrl);
    
    // Restore segments with timing data if available, otherwise fallback to text
    if (transcription.segments && transcription.segments.length > 0) {
      setCurrentTranscription(transcription.segments);
    } else {
      // Fallback for older transcriptions without segments
      setCurrentTranscription([{
        text: transcription.text,
        type: 'final'
      }]);
    }
  };

  return (
    <Fade in timeout={300}>
    <Box sx={{
      height: '100vh',
      display: 'flex',
      flexDirection: 'column',
      overflow: 'hidden',
      maxWidth: '100vw',
      p: 2
    }}>
      {/* Header */}
      <Box
        sx={{
          padding: { xs: 2, md: 3 },
          borderRadius: '16px',
          backgroundColor: isDark ? 'rgba(255, 255, 255, 0.1)' : 'background.paper',
          backdropFilter: 'blur(10px)',
          boxShadow: isDark ? '0 8px 32px rgba(0, 0, 0, 0.1)' : '0 8px 32px rgba(0, 0, 0, 0.06)',
          border: isDark ? '1px solid rgba(255, 255, 255, 0.2)' : '1px solid',
          borderColor: isDark ? undefined : 'divider',
          display: 'flex',
          flexDirection: { xs: 'column', md: 'row' },
          gap: { xs: 3, md: 4 },
          alignItems: { xs: 'stretch', md: 'center' },
          justifyContent: 'space-between',
          mb: 2,
          flexShrink: 0
        }}
      >
        <Box sx={{ flex: { xs: 'none', md: '1' }, mb: { xs: 2, md: 0 } }}>
          <Typography 
            variant="h4" 
            sx={{ 
              fontWeight: 700,
              background: tokens.gradients.primary,
              backgroundClip: 'text',
              WebkitBackgroundClip: 'text',
              color: 'transparent',
              mb: 1,
              fontSize: { xs: '1.8rem', md: '2.125rem' }
            }}
          >
{t.welcome}
          </Typography>
          <Typography 
            variant="body1" 
            sx={{ 
              color: isDark ? 'rgba(255, 255, 255, 0.8)' : 'text.secondary',
              fontSize: { xs: '1rem', md: '1.1rem' },
              fontWeight: 400
            }}
          >
{t.subtitle}
          </Typography>
        </Box>

        <FormControl sx={{ minWidth: { xs: 200, md: 250 }, maxWidth: 300, mb: { xs: 2, md: 0 } }}>
          <InputLabel 
            id="language-select-label"
            sx={{
              color: isDark ? 'rgba(255, 255, 255, 0.7)' : 'text.secondary',
              '&.Mui-focused': {
                color: isDark ? '#00CFF3' : tokens.colors.primary.darkText
              }
            }}
          >
            {t.language}
          </InputLabel>
          <Select
            labelId="language-select-label"
            id="language-select"
            value={language}
            label={t.language}
            onChange={(e) => handleLanguageChangeWithUI(e.target.value as ASRLanguage)}
            fullWidth
            sx={{
              borderRadius: 3,
              backgroundColor: isDark ? 'rgba(255, 255, 255, 0.05)' : 'background.paper',
              '& .MuiSelect-select': {
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                py: 1.5,
                fontSize: '1rem',
                color: isDark ? 'rgba(255, 255, 255, 0.9)' : 'text.primary'
              },
              '& .MuiOutlinedInput-notchedOutline': {
                borderColor: isDark ? 'rgba(255, 255, 255, 0.2)' : 'divider',
                borderRadius: 3
              },
              '&:hover .MuiOutlinedInput-notchedOutline': {
                borderColor: isDark ? 'rgba(255, 255, 255, 0.4)' : 'text.secondary'
              },
              '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                borderColor: isDark ? '#00CFF3' : tokens.colors.primary.darkText
              },
              '& .MuiSvgIcon-root': {
                color: isDark ? 'rgba(255, 255, 255, 0.7)' : 'text.secondary'
              }
            }}
          >
            <MenuItem value="fr-FR" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <span style={{ fontSize: '18px' }}>🇫🇷</span>
              {t.languages['fr-FR']}
            </MenuItem>
            <MenuItem value="fr-medicalV2-32" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <span style={{ fontSize: '18px' }}>🇫🇷🏥</span>
              Medical V2
            </MenuItem>
            <MenuItem value="en-US" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <span style={{ fontSize: '18px' }}>🇺🇸</span>
              {t.languages['en-US']}
            </MenuItem>
            <MenuItem value="de-DE" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <span style={{ fontSize: '18px' }}>🇩🇪</span>
              {t.languages['de-DE']}
            </MenuItem>
            <MenuItem value="nl-NL" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <span style={{ fontSize: '18px' }}>🇳🇱</span>
              {t.languages['nl-NL']}
            </MenuItem>
            <MenuItem value="it-IT" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <span style={{ fontSize: '18px' }}>🇮🇹</span>
              {t.languages['it-IT']}
            </MenuItem>
            <MenuItem value="es-ES" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <span style={{ fontSize: '18px' }}>🇪🇸</span>
              {t.languages['es-ES']}
            </MenuItem>
            <MenuItem value="sv-SE" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <span style={{ fontSize: '18px' }}>🇸🇪</span>
              {t.languages['sv-SE']}
            </MenuItem>
            <MenuItem value="pt-PT" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <span style={{ fontSize: '18px' }}>🇵🇹</span>
              {t.languages['pt-PT']}
            </MenuItem>
            <MenuItem value="pl-PL" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <span style={{ fontSize: '18px' }}>🇵🇱</span>
              {t.languages['pl-PL']}
            </MenuItem>
            <MenuItem value="ja-JP" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <span style={{ fontSize: '18px' }}>🇯🇵</span>
              {t.languages['ja-JP']}
            </MenuItem>
            <MenuItem value="he-IL" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <span style={{ fontSize: '18px' }}>🇮🇱</span>
              {t.languages['he-IL']}
            </MenuItem>
            <MenuItem value="tr-TR" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <span style={{ fontSize: '18px' }}>🇹🇷</span>
              {t.languages['tr-TR']}
            </MenuItem>
          </Select>
        </FormControl>

        <Box
          sx={{
            display: 'flex',
            flexDirection: { xs: 'column', sm: 'row' },
            justifyContent: { xs: 'center', md: 'flex-end' },
            alignItems: { xs: 'stretch', sm: 'center' },
            gap: 2,
            mt: { xs: 2, md: 0 },
            width: { xs: '100%', md: 'auto' }
          }}
        >
          <Tooltip title={t.uploadAudioFile}>
            <label htmlFor="contained-button-file">
              <input
                accept="audio/*"
                id="contained-button-file"
                type="file"
                style={{ display: 'none' }}
                onChange={handleFileUpload}
              />
              <Button
                variant="contained"
                startIcon={loading ? undefined : <FileUploadIcon />}
                component="span"
                size="large"
                disabled={loading || isRecording}
                sx={{
                  borderRadius: 3,
                  px: 3,
                  py: 1.5,
                  background: loading
                    ? 'linear-gradient(45deg, #888, #666)'
                    : tokens.gradients.primary,
                  '&:hover': {
                    background: loading
                      ? 'linear-gradient(45deg, #888, #666)'
                      : tokens.gradients.primaryHover
                  },
                  textTransform: 'none',
                  fontWeight: 700,
                  fontSize: '1rem',
                  minWidth: { xs: 'auto', sm: '140px' },
                  width: { xs: '100%', sm: 'auto' }
                }}
              >
{loading ? t.processing : t.uploadFile}
              </Button>
            </label>
          </Tooltip>

          <Tooltip title={t.recordWithMicrophone}>
            <Button
              variant="contained"
              onClick={!isRecording ? handleStartRecord : stopRecording}
              startIcon={loading && !isRecording ? undefined : (!isRecording ? <MicIcon /> : <StopCircleIcon />)}
              size="large"
              disabled={loading && !isRecording}
              sx={{
                borderRadius: 3,
                px: 3,
                py: 1.5,
                background: loading && !isRecording
                  ? 'linear-gradient(45deg, #888, #666)'
                  : isRecording 
                    ? 'linear-gradient(45deg, #FF5722, #F44336)'
                    : 'linear-gradient(45deg, #4CAF50, #388E3C)',
                '&:hover': {
                  background: loading && !isRecording
                    ? 'linear-gradient(45deg, #888, #666)'
                    : isRecording
                      ? 'linear-gradient(45deg, #F44336, #D32F2F)'
                      : 'linear-gradient(45deg, #388E3C, #2E7D32)'
                },
                textTransform: 'none',
                fontWeight: 700,
                fontSize: '1rem',
                minWidth: { xs: 'auto', sm: '160px' },
                width: { xs: '100%', sm: 'auto' }
              }}
            >
{loading && !isRecording ? t.processing : (isRecording ? t.stopRecording : t.startRecording)}
            </Button>
          </Tooltip>
        </Box>
      </Box>


      {/* Main Content */}
      <Box sx={{ 
        flex: 1, 
        display: 'flex', 
        flexDirection: { xs: 'column', md: 'row' },
        gap: 2, 
        overflow: 'hidden',
        minHeight: 0
      }}>
        {/* Left Panel - Previous Transcriptions */}
        <Paper
          sx={{
            width: { xs: '100%', md: 350 },
            height: { xs: 200, md: 'auto' },
            backgroundColor: isDark ? 'rgba(255, 255, 255, 0.05)' : 'background.paper',
            backdropFilter: 'blur(10px)',
            border: '1px solid',
            borderColor: isDark ? 'rgba(255, 255, 255, 0.1)' : 'divider',
            borderRadius: 3,
            overflow: 'hidden',
            display: 'flex',
            flexDirection: 'column',
            flexShrink: 0
          }}
        >
          <TranscriptionHistory 
            key={historyRefreshKey}
            language={language}
            onTranscriptionSelect={handleTranscriptionSelect} 
          />
        </Paper>

        {/* Right Panel - Current Transcription */}
        <Paper
          sx={{
            flex: 1,
            backgroundColor: isDark ? 'rgba(255, 255, 255, 0.05)' : 'background.paper',
            backdropFilter: 'blur(10px)',
            border: '1px solid',
            borderColor: isDark ? 'rgba(255, 255, 255, 0.1)' : 'divider',
            borderRadius: 3,
            overflow: 'hidden',
            display: 'flex',
            flexDirection: 'column',
            minHeight: 0
          }}
        >
          {/* Transcription Header */}
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: 1,
              px: 2,
              height: 48,
              borderBottom: '1px solid',
              borderBottomColor: isDark ? 'rgba(255, 255, 255, 0.1)' : 'divider'
            }}
          >
              <Typography
                variant="subtitle2"
                sx={{
                  fontWeight: 700,
                  color: isDark ? 'rgba(255, 255, 255, 0.9)' : tokens.colors.primary.darkText,
                  textTransform: 'uppercase',
                  letterSpacing: '0.05em',
                  fontSize: '0.7rem',
                }}
              >
                {t.liveTranscription}
              </Typography>
              
              <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center' }}>
              <Tooltip title={dictationMode ? t.dictationModeOn : t.dictationModeOff}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={dictationMode}
                      onChange={handleDictationToggle}
                      disabled={isRecording}
                      size="small"
                      inputProps={{ 'aria-label': dictationMode ? t.dictationModeOn : t.dictationModeOff }}
                      sx={{
                        '& .MuiSwitch-switchBase.Mui-checked': {
                          color: isDark ? '#00CFF3' : tokens.colors.primary.darkText,
                        },
                        '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                          backgroundColor: isDark ? '#00CFF3' : tokens.colors.primary.darkText,
                        },
                        '& .MuiSwitch-switchBase.Mui-disabled': {
                          color: isDark ? 'rgba(255,255,255,0.3)' : 'action.disabled',
                        },
                        '& .MuiSwitch-switchBase.Mui-disabled + .MuiSwitch-track': {
                          backgroundColor: isDark ? 'rgba(255,255,255,0.1)' : 'action.disabledBackground',
                        },
                      }}
                    />
                  }
                  label={
                    <RecordVoiceOverIcon
                      sx={{
                        color: isRecording
                          ? (isDark ? 'rgba(255,255,255,0.3)' : 'text.disabled')
                          : dictationMode
                            ? (isDark ? '#00CFF3' : tokens.colors.primary.darkText)
                            : (isDark ? 'rgba(255,255,255,0.5)' : 'text.secondary')
                      }}
                    />
                  }
                  sx={{ ml: 0, mr: 0.5 }}
                />
              </Tooltip>
              <Tooltip title={t.decreaseTextSize}>
                <IconButton
                  onClick={decreaseFontSize}
                  sx={{ color: isDark ? 'rgba(255, 255, 255, 0.7)' : 'text.secondary' }}
                >
                  <TextDecreaseIcon />
                </IconButton>
              </Tooltip>
              
              <Tooltip title={t.increaseTextSize}>
                <IconButton
                  onClick={increaseFontSize}
                  sx={{ color: isDark ? 'rgba(255, 255, 255, 0.7)' : 'text.secondary' }}
                >
                  <TextIncreaseIcon />
                </IconButton>
              </Tooltip>
              
              <Tooltip title={t.copyText}>
                <IconButton
                  onClick={copyTranscription}
                  sx={{ color: isDark ? 'rgba(255, 255, 255, 0.7)' : 'text.secondary' }}
                >
                  <ContentCopyIcon />
                </IconButton>
              </Tooltip>
              
              {currentAudioUrl && !isRecording && (
                <Tooltip title={isPlaying ? t.pause : t.playAudio}>
                  <IconButton
                    onClick={() => {
                      if (audioPlayerRef.current) {
                        if (audioPlayerRef.current.paused) {
                          audioPlayerRef.current.play().catch(error => {
                            setIsPlaying(false);
                          });
                        } else {
                          audioPlayerRef.current.pause();
                        }
                      }
                    }}
                    sx={{ color: isDark ? 'rgba(255, 255, 255, 0.7)' : 'text.secondary' }}
                  >
                    {isPlaying ? <PauseIcon /> : <PlayArrowIcon />}
                  </IconButton>
                </Tooltip>
              )}
              
              <Tooltip title={t.teleprompter}>
                <IconButton
                  onClick={() => setShowTeleprompter(true)}
                  sx={{
                    color: isDark ? '#00CFF3' : tokens.colors.primary.darkText,
                    backgroundColor: isDark ? 'rgba(0, 207, 243, 0.1)' : tokens.colors.primary[50],
                    '&:hover': {
                      backgroundColor: isDark ? 'rgba(0, 207, 243, 0.2)' : tokens.colors.primary[100],
                      transform: 'scale(1.1)'
                    },
                    transition: 'all 0.2s ease-in-out',
                    ml: 1
                  }}
                >
                  <TvIcon />
                </IconButton>
              </Tooltip>
              </Box>
          </Box>

          {/* Transcription Display */}
          <Box sx={{ flex: 1, overflow: 'hidden', display: 'flex' }}>
            <LiveTranscription
              segments={currentTranscription}
              isTranscribing={isRecording || loading}
              language={language}
              fontSize={fontSize}
              audioRef={audioPlayerRef}
              onTimecodeClick={(time) => {
                if (audioPlayerRef.current && currentAudioUrl) {
                  audioPlayerRef.current.currentTime = time;
                  audioPlayerRef.current.play().catch(error => {
                    // Handle play error silently
                  });
                }
              }}
            />
          </Box>
        </Paper>
      </Box>


      {/* Hidden audio element for playback */}
      {currentAudioUrl && (
        <audio 
          ref={audioPlayerRef}
          src={currentAudioUrl}
          onPlay={() => setIsPlaying(true)}
          onPause={() => setIsPlaying(false)}
          onEnded={() => setIsPlaying(false)}
          onError={(e) => {
            setIsPlaying(false);
          }}
          style={{ display: 'none' }}
        />
      )}

      {/* Teleprompter Overlay */}
      <TeleprompterOverlay
        text={getTeleprompterText()}
        isOpen={showTeleprompter}
        onClose={() => setShowTeleprompter(false)}
        isRecording={isRecording}
        teleprompterTitle={t.teleprompter}
        waitingText={t.waitingForTranscription}
      />
    </Box>
    </Fade>
  );
};

export default Form;
